// js/shared/card-rows.js — WHICH ROWS THE CONTACT CARD IS ALLOWED TO SHOW.
//
// QUEUE ITEM IE. `/card` is the page behind the QR code on the printed business
// card. Five of its rows are always there and are written in the markup (Call,
// Text, Email, Ask for a free call, Website) because a page a stranger
// reaches by scanning a code in a parking lot has to work before any script
// does. Three more are CONDITIONAL: "Leave a Google review" and the two "Get
// the app" links exist only once the trainer has pasted those addresses into
// Settings › Business, and until then they are not on the page at all.
//
// THAT DECISION IS THE WHOLE MODULE, and it is here rather than in card.html
// for the reason every model in this repo is split out: a rule that only a
// rendered page can exercise is a rule nobody can prove is still a rule
// (CI-META-1i). The page fetches a row and paints what this returns; it judges
// nothing.
//
// ── AN EMPTY SLOT IS NOT A SLOT ────────────────────────────────────────────
//
// R17's shape, applied to a link: a "Leave a Google review" row that goes
// nowhere is worse than no row, because the reader taps it. So a slot with no
// address, a blank one, or one whose value is not an `http(s)` URL is ABSENT —
// not disabled, not greyed, not present-with-a-toast. The trainer sets it and
// it appears; he clears it and it goes.
//
// ── WHY THE URL IS CHECKED AT ALL, when the trainer types it himself ───────
//
// `javascript:` and `data:` in an `href` are script, and the value travels
// trainer_settings → a view → an anonymous page with no session. The trainer is
// not the threat; a settings row that can put arbitrary script in front of a
// stranger is still a shape worth refusing, and admin.html already applies the
// identical `^https?:` test to `trainers.photo_url` before rendering it. One
// rule, both surfaces.
//
// PURE. No DOM, no fetch.

/** An `http(s)` URL, trimmed, or null for anything else (including blank). */
export function safeUrl(value) {
  const raw = String(value == null ? '' : value).trim();
  if (!raw) return null;
  return /^https?:\/\/\S+$/i.test(raw) ? raw : null;
}

/**
 * The conditional rows, in the order they are shown, for a `card_profile` row.
 *
 * Each entry is `{ id, href }` — `id` is the ANCHOR's, and the page reveals the
 * list item around it — and NOT a label. The words live in card.html's
 * markup under `data-i18n`, painted by the same shared painter that writes
 * every other string on the page — so a model that also carried them would be
 * a second copy of a sentence that is already somewhere, which is the drift
 * js/shared/settings-registry.js opens by warning about. What this owns is the
 * DECISION: which slot, in which order, pointing where.
 *
 * `profile` may be null (the fetch failed, or the reader is offline): the answer
 * is an empty list, which is the page as it renders with no script at all.
 */
export function optionalRows(profile) {
  const p = profile || {};
  const rows = [];
  const review = safeUrl(p.google_review_url);
  if (review) rows.push({ id: 'card-row-review', href: review });
  const ios = safeUrl(p.app_store_url);
  if (ios) rows.push({ id: 'card-row-ios', href: ios });
  const android = safeUrl(p.play_store_url);
  if (android) rows.push({ id: 'card-row-android', href: android });
  return rows;
}

/**
 * The photo to show, or null for "draw the mark instead".
 *
 * `trainers.photo_url` is NULL until a headshot is uploaded (migration
 * 20261015000000), and NULL is the normal state — so the mark is not a fallback
 * for an error, it is the other half of a two-state design. The same
 * `^https?:` filter as every other address on this page.
 */
export function cardPhotoUrl(profile) {
  return safeUrl((profile || {}).photo_url);
}

/**
 * QUEUE ITEM KVb: one of our own links, in the card's language. `?lang=es`
 * FIRST when Spanish, no `lang` at all when English, and every other parameter
 * and the hash kept exactly. "Ask for a free call" is
 * `/?utm_source=card&utm_medium=qr#contact` in English and
 * `/?lang=es&utm_source=card&utm_medium=qr#contact` in Spanish. Pure; a
 * same-origin path in, a same-origin path out (card-page.js paints it).
 */
export function cardLangHref(href, lang) {
  const url = new URL(String(href), 'https://kinetiqaf.com');
  const rest = [...url.searchParams.entries()].filter(([k]) => k !== 'lang');
  const params = new URLSearchParams(String(lang).toLowerCase().startsWith('es') ? [['lang', 'es'], ...rest] : rest);
  const q = params.toString();
  return url.pathname + (q ? '?' + q : '') + url.hash;
}
